import React from 'react';
import { Trans } from 'react-i18next';
import { ReferenceDiscount } from '@1po/1po-bff-fe-spec/generated/catalog/trading_data/model/ReferenceDiscount';
import { VehicleDetail } from '@1po/1po-bff-fe-spec/generated/order/response/GetDealerOrderPageResponse';
import { OrderReferenceItem } from '@1po/1po-bff-fe-spec/generated/order/response/model/OrderItem';
import { Document, Page, Text as PdfText, View } from '@react-pdf/renderer';
import { t } from 'i18next';
import { getDiscountColor } from 'components/Discount';
import {
  LeadText,
  OrderDetailTableCell,
  PageNumber,
  Spacer,
  Table,
  Text,
  TextMid,
  VerticalSpacer,
} from 'components/Pdf/PdfUI';
import { fonts, styles } from 'components/Pdf/PdfUI.styles';
import { getDeliveryTypeLabel } from 'domains/order/OrderDelivery';
import { getStatusLabel } from 'domains/order/OrderStatus';
import { theme } from 'styles';
import { isQueryBMM } from 'utils';
import { textFormatter } from 'utils/format';
import { Header } from './Header';
import { OrderPdfData } from './MyOrdersDetailPdfPage';
import { OrderContactCard } from './OrderContactCard';
import { OrderInformationCard } from './OrderInformationCard';
import { TotalCard } from './TotalCard';

export const InstructionsCard = ({ data }: { data: OrderPdfData }) => {
  return (
    <View
      style={[
        styles.row,
        {
          backgroundColor: theme.color.primary_brand_bg,
          color: 'black',
          padding: '0 8px',
          height: '20px',
          alignItems: 'center',
          gap: '5px',
        },
      ]}
    >
      <PdfText style={[fonts.input_text, fonts.bold]}>{t('my_orders.pdf.instructions', 'Instructions')}:</PdfText>
      <PdfText style={[fonts.input_text]}>{data.order.instruction}</PdfText>
    </View>
  );
};

const getInfoText = (label: React.ReactNode, data: React.ReactNode, vehicle?: boolean) => {
  return (
    <View style={[styles.row, { alignItems: 'center', height: '15px' }]}>
      <TextMid>
        {label}
        {!vehicle && ':'}
      </TextMid>
      {!vehicle && <VerticalSpacer width={5} />}
      <TextMid>{data}</TextMid>
    </View>
  );
};

const getCarIdentification = (vehicleDetail: VehicleDetail) => {
  const isBMM = isQueryBMM(vehicleDetail.vehicleKey);
  return isBMM ? (
    <></>
  ) : (
    getInfoText(
      <>
        {vehicleDetail.vehicleIdentifiedBy && (
          <>
            <Trans i18nKey={'catalog.vehicle.vin'}>VIN</Trans>
            {`: ${vehicleDetail.vin}`}
          </>
        )}
      </>,
      <></>,
      true,
    )
  );
};
const getInfoVehicle = (vehicleDetail: VehicleDetail | undefined, data: OrderPdfData) => {
  const isDatahubVehicle = vehicleDetail?.catalogSource === 'DATAHUB';
  const isIamVehicle = vehicleDetail?.catalogSource === 'IAM';
  const dataHubVehicleData = vehicleDetail?.dataHubVehicle;
  const iamVehicleData = vehicleDetail?.iamVehicle;

  return (
    <View style={[styles.column, { alignItems: 'flex-end', gap: '-3px' }]}>
      {data.order.orderMark && getInfoText(data.text.orderLine, data.order.orderMark)}
      {vehicleDetail && (
        <>
          {getCarIdentification(vehicleDetail)}
          {isDatahubVehicle && dataHubVehicleData && (
            <>
              {getInfoText(<></>, dataHubVehicleData.name, true)}
              {getInfoText(<></>, dataHubVehicleData.modelType, true)}
            </>
          )}
          {isIamVehicle && iamVehicleData && (
            <>
              {iamVehicleData.name && getInfoText(iamVehicleData.name, <></>, true)}
              {iamVehicleData.modelType && getInfoText(<></>, iamVehicleData.modelType, true)}
            </>
          )}
        </>
      )}
    </View>
  );
};

const InformationHeader = ({ data }: { data: OrderPdfData }) => {
  return (
    <View
      style={[
        styles.row,
        { width: '100%', backgroundColor: theme.color.primary_brand_bg, padding: '3px 10px 3px 10px' },
      ]}
    >
      <View style={[styles.column, { gap: '-3px' }]}>
        {getInfoText(data.text.client, data.order.userInfo.garageInfo?.name ?? '')}
      </View>
      <View style={[styles.row_reverse]}>{getInfoVehicle(data.order.vehicleDetail, data)}</View>
    </View>
  );
};

const getEffectiveUnitPriceVatExcluded = (reference: OrderReferenceItem): number => {
  const { price } = reference;
  return price?.discount ? Number(price.unitPriceVatExcluded) : Number(price.originalUnitPriceVatExcluded);
};

const referenceTableStyles = {
  headerCell: {
    backgroundColor: theme.color.primary_brand_bg,
    ...fonts.text_bold,
    fontFamily: 'NouvelR',
    fontWeight: 600,
  },
  wrapper: {
    borderRight: `1px solid ${theme.color.grey95}`,
  },
  row: {
    borderBottom: `1px solid ${theme.color.grey95}`,
  },
  cell: {
    borderLeft: `1px solid ${theme.color.grey95}`,
    minHeight: '19px',
    paddingLeft: '4px',
    paddingRight: '4px',
    justifyContent: 'flex-start',
  } as const,
};

const buildReferenceColumns = (data: OrderPdfData) => [
  {
    title: data.text.referenceHeader,
    width: 65,
    render: () => <></>,
    renderNode: (item: OrderReferenceItem) => (
      <OrderDetailTableCell>
        <Text>{item.referenceNumber}</Text>
      </OrderDetailTableCell>
    ),
  },
  {
    title: data.text.designation,
    width: 135,
    render: () => <></>,
    renderNode: (item: OrderReferenceItem) => (
      <OrderDetailTableCell>
        <Text>{item.name}</Text>
      </OrderDetailTableCell>
    ),
  },
  {
    title: data.text.referenceMarkHeader,
    width: 80,
    render: () => <></>,
    renderNode: (item: OrderReferenceItem) => (
      <OrderDetailTableCell>
        <Text>{item.referenceMark ?? ''}</Text>
      </OrderDetailTableCell>
    ),
  },
  {
    title: data.text.quantityHeader,
    width: 35,
    render: () => <></>,
    renderNode: (item: OrderReferenceItem) => (
      <OrderDetailTableCell centered>
        <Text>{item.quantity}</Text>
      </OrderDetailTableCell>
    ),
  },
  {
    title: data.text.deliveryMode,
    width: 80,
    render: () => <></>,
    renderNode: (item: OrderReferenceItem) =>
      item.deliveryType ? (
        <OrderDetailTableCell>
          <Text>{getDeliveryTypeLabel(item.deliveryType)}</Text>
        </OrderDetailTableCell>
      ) : (
        <Text />
      ),
  },
  {
    title: data.text.unitPrice,
    width: 80,
    render: () => <></>,
    renderNode: (item: OrderReferenceItem) => {
      const { price } = item;
      const discountColor = getDiscountColor({
        referenceNumber: '',
        discount: price.discount,
        promotionType: price.promotionType,
        discountType: price.discountType,
      } as ReferenceDiscount);
      return (
        <View style={[styles.col, { alignItems: 'center', width: '100%' }]}>
          <PdfText style={[fonts.textMid, { textDecoration: price?.discount ? 'line-through' : 'none' }]}>
            {data.text.vatExcl(
              textFormatter.formatCurrency(Number(price.originalUnitPriceVatExcluded), price.currency),
            )}
          </PdfText>
          {price?.discount && (
            <>
              <PdfText style={[fonts.textMid, { color: discountColor, marginTop: '4px' }]}>
                {data.text.vatExcl(textFormatter.formatCurrency(Number(price.unitPriceVatExcluded), price.currency))}
              </PdfText>
              <PdfText style={[fonts.text_bold, { color: discountColor, marginTop: '2px' }]}>
                {data.text.discount(price.discount.toString())}
              </PdfText>
            </>
          )}
        </View>
      );
    },
  },
  {
    title: data.text.totalPrice,
    width: 80,
    render: () => <></>,
    renderNode: (item: OrderReferenceItem) => {
      const totalPrice = getEffectiveUnitPriceVatExcluded(item) * Number(item.quantity);
      return (
        <OrderDetailTableCell centered>
          <Text>{data.text.vatExcl(textFormatter.formatCurrency(totalPrice, item.price.currency))}</Text>
        </OrderDetailTableCell>
      );
    },
  },
];

const ReferencesTable = ({ data }: { data: OrderPdfData }) => (
  <Table
    data={data.order.orderedReferences}
    columns={buildReferenceColumns(data)}
    maxWidth={555}
    tableStyles={referenceTableStyles}
    boldDefaultHeader
  />
);

export const MyOrdersDetailPdfPage = ({ data }: { data: OrderPdfData }) => {
  return (
    <Document>
      <Page size={'A4'} style={styles.page} wrap>
        <PageNumber />
        <View style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
          <View style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
            <View>
              <Header data={data} />
            </View>
            <View style={{ textAlign: 'right', alignSelf: 'flex-end' }}>
              <LeadText>
                {data.text.status}
                {`: `}
                {getStatusLabel(data.order.presentableOrderStatus)}
              </LeadText>
            </View>
          </View>
          <Spacer />
          <InformationHeader data={data} />
          <Spacer />
          <ReferencesTable data={data} />
          <View style={{ flexGrow: 1 }} />
          {data.order.instruction && <InstructionsCard data={data} />}
          <View style={[styles.row, { justifyContent: 'space-between', gap: '12px', marginTop: '12px' }]}>
            <OrderInformationCard
              text={data.text}
              orderedBy={`${data.order.userInfo.firstName} ${data.order.userInfo.lastName}`}
              clientNumber={data.order.userInfo.buyerId}
              distributor={data.order.userInfo.sellerName}
              billingAccount={data.order.userInfo.clientCode}
            />
            <OrderContactCard text={data.text} garageInfo={data.order.userInfo.garageInfo} />
            <TotalCard
              text={data.text}
              values={{
                priceVatExcluded: data.order.priceVatExcluded,
                priceVat: data.order.priceVat,
                priceVatIncluded: data.order.priceVatIncluded,
                currency: data.order.currency,
              }}
            />
          </View>
        </View>
      </Page>
    </Document>
  );
};
